import { useState, useEffect, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useTenant } from "@/lib/hooks/useTenant";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import {
  Building2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Image,
} from "lucide-react";

interface TenantConfig {
  cnpj?: string | null;
  address?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  favicon_url?: string | null;
}

export function SettingsEmpresaPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { data: tenantBilling, isLoading, isError } = useTenant();
  const tenant = tenantBilling?.tenant ?? null;

  const tenantConfig = (tenant?.config as TenantConfig | undefined) ?? {};

  const [tenantName, setTenantName] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (tenant) {
      setTenantName(tenant.name ?? "");
      setCnpj(tenantConfig.cnpj ?? "");
      setAddress(tenantConfig.address ?? "");
    }
  }, [tenant?.id]);

  const handleSave = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!tenant) return;

    setSaving(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    const normalizedName = tenantName.trim();
    if (!normalizedName) {
      setErrorMessage("O nome da empresa é obrigatório.");
      setSaving(false);
      return;
    }

    const { error: updateError } = await supabase
      .from("tenants")
      .update({
        name: normalizedName,
        config: {
          ...tenantConfig,
          cnpj: cnpj.trim() || null,
          address: address.trim() || null,
        },
      })
      .eq("id", tenant.id);

    setSaving(false);

    if (updateError) {
      setErrorMessage("Não foi possível salvar as alterações.");
      return;
    }

    setSuccessMessage("Dados da empresa salvos com sucesso.");
    await queryClient.invalidateQueries({ queryKey: ["tenant-billing"] });
  };

  const error =
    isError ? "Não foi possível carregar os dados do tenant." : null;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <PageHeader
        title="Empresa"
        subtitle="Informações da sua empresa no LucraLink."
      />

      {error && (
        <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle size={15} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {isLoading && (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="rounded-xl border border-border bg-card overflow-hidden"
            >
              <div className="p-5 space-y-3">
                <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                <div className="h-9 w-full animate-pulse rounded bg-muted/60" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && tenant && (
        <form onSubmit={handleSave} className="space-y-6">
          {/* Card: Logo / Avatar */}
          <section
            className="rounded-xl border border-border bg-card overflow-hidden"
            aria-labelledby="section-logo"
          >
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border bg-[hsl(var(--surface-raised)/0.4)]">
              <Image size={14} className="text-muted-foreground" />
              <h2
                id="section-logo"
                className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
              >
                Logo da empresa
              </h2>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-raised">
                  <Building2 size={24} className="text-muted-foreground" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">
                    Logo / Avatar
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Upload de logo será disponível em breve. Por enquanto, seu
                    avatar é gerado automaticamente.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Card: Dados da empresa */}
          <section
            className="rounded-xl border border-border bg-card overflow-hidden"
            aria-labelledby="section-dados"
          >
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border bg-[hsl(var(--surface-raised)/0.4)]">
              <Building2 size={14} className="text-muted-foreground" />
              <h2
                id="section-dados"
                className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
              >
                Dados da empresa
              </h2>
            </div>
            <div className="p-5 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tenant-name" variant="field">
                  Nome da empresa
                </Label>
                <Input
                  id="tenant-name"
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                  placeholder="Minha Empresa Ltda"
                  autoComplete="off"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tenant-cnpj" variant="field">
                  CNPJ{" "}
                  <span className="text-muted-foreground/60">(opcional)</span>
                </Label>
                <Input
                  id="tenant-cnpj"
                  value={cnpj}
                  onChange={(e) => setCnpj(e.target.value)}
                  placeholder="00.000.000/0001-00"
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  Seu CNPJ será exibido apenas para você e não é compartilhado.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tenant-address" variant="field">
                  Endereço{" "}
                  <span className="text-muted-foreground/60">(opcional)</span>
                </Label>
                <Input
                  id="tenant-address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Av. Paulista, 1000, São Paulo - SP"
                  autoComplete="off"
                />
              </div>
            </div>
          </section>

          {/* Feedback messages */}
          {successMessage && (
            <div className="flex items-center gap-3 rounded-lg border border-primary/25 bg-primary/10 px-4 py-3 text-sm text-primary">
              <CheckCircle2 size={15} className="shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          {errorMessage && (
            <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <AlertCircle size={15} className="shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end">
            <Button type="submit" disabled={saving}>
              {saving ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Building2 size={15} />
              )}
              Salvar
            </Button>
          </div>
        </form>
      )}

      {!isLoading && !tenant && !error && (
        <div className="rounded-xl border border-border bg-card p-10 text-center">
          <p className="text-sm text-muted-foreground">
            Nenhum tenant encontrado para esta conta.
          </p>
        </div>
      )}
    </div>
  );
}
